import React, { useEffect, useMemo, useState } from 'react';
import { useFilters } from '@panwds/react-filters';
import dayjs from '../../common-resources/dayjs';
import { useFetchIncidents } from '../../services/api/incidents';
import { parseRegionFilterSelections } from '../../shared/utils/regions';
import commonStyles from '../../scss/StatusPageCommon.module.scss';
import { isEmpty } from 'lodash';
import ProductUptimeBar from './components/ProductUptimeBar.jsx';
import { useFetchRegions } from '../../services/api/regions';

const FILTER_CONTEXT_ID = 'StatusPageFilterBar';

const ProductStatus = props => {
    const { products, isLoadingProducts, isHistoricView, customHistoricRange, onHelpClick } = props;
    const { data: allRegions } = useFetchRegions();
    const { filters: pageFilters, initialized: isFilterBarInitialized } = useFilters(FILTER_CONTEXT_ID);

    const buildUptimeQuery = (filters) => {
        const selectedRegions = filters?.region || [];
        const parsedRegionSelections = isEmpty(selectedRegions) ? null : parseRegionFilterSelections(selectedRegions);
        const selectedStatusType = filters?.statusType?.toLowerCase() || 'all';
        const regionQueryParts = [];
        if (parsedRegionSelections) {
            const { selectedContinents, selectedCountries, selectedCities } = parsedRegionSelections;
            if (selectedContinents?.length) regionQueryParts.push(`continent=${selectedContinents.join(',')}`);
            if (selectedCountries?.length) regionQueryParts.push(`country=${selectedCountries.join(',')}`);
            if (selectedCities?.length) regionQueryParts.push(`city=${selectedCities.join(',')}`);
            regionQueryParts.push('region_filter_mode=or');
        }

        // startDate and timeRange are intentionally omitted from the standard API query — the
        // backend returns all incidents (unbounded) so that long-running ongoing incidents are
        // never excluded. The timeRange filter value is passed to ProductUptimeBar separately
        // and controls only the visual display window, not what the API fetches.
        // For custom date ranges, startDate+endDate define the explicit window and are kept.
        let dateParts = [];
        if (filters?.timeRange === 'custom' && customHistoricRange?.start && customHistoricRange?.end) {
            const inclusiveEnd = dayjs(customHistoricRange.end).add(1, 'day').format('YYYY-MM-DD');
            dateParts = [`startDate=${customHistoricRange.start}`, `endDate=${inclusiveEnd}`];
        }

        const queryParts = [...dateParts, ...regionQueryParts];
        if (filters?.products?.length) {
            queryParts.push(`product_type=${filters.products.join(',')}`);
        }
        if (['degraded', 'disruption'].includes(selectedStatusType)) {
            queryParts.push(`severity=${selectedStatusType}`);
        }
        return queryParts.join('&');
    };

    const [incidentQuery90d, setIncidentQuery90d] = useState(null);

    const { data: incidentsData90d, isLoading: isLoadingUptime, isFetched: isIncidentsFetched } = useFetchIncidents({
        query: incidentQuery90d,
        isEnabled: incidentQuery90d !== null && isFilterBarInitialized && !!isHistoricView,
        gcTime: 5 * 60 * 1000,
        refetchInterval: 10 * 60 * 1000,
    });

    // build query
    useEffect(() => {
        if (!isFilterBarInitialized) {
            return;
        }
        setIncidentQuery90d(buildUptimeQuery(pageFilters));
    }, [isFilterBarInitialized, pageFilters, customHistoricRange]);

    const incidents90d = useMemo(() => {
        return incidentsData90d;
    }, [incidentsData90d]);

    const filteredProducts = useMemo(() => {
        if (isEmpty(products)) {
            return [];
        }

        const selectedProducts = pageFilters?.products || [];

        if (isEmpty(selectedProducts)) {
            return products || [];
        }

        return products.filter(item => {
            return selectedProducts.includes(item.product_type);
        });
    }, [products, pageFilters?.products]);

    const hasProductFilter = pageFilters?.products?.length > 0;
    const hideProductUptimeBar = hasProductFilter && isEmpty(filteredProducts);

    return (
        <div className="ssmutw-p-0">
            {!hideProductUptimeBar && (
                <div className={`ssmutw-dark tw-dark ssmutw-text-left ssmutw-py-4 ${commonStyles.contentWidth}`}>
                    <ProductUptimeBar
                        incidents={incidents90d}
                        isLoading={isLoadingUptime || !isIncidentsFetched}
                        isLoadingProducts={isLoadingProducts}
                        products={filteredProducts}
                        allRegions={allRegions}
                        timeRange={pageFilters?.timeRange || '90d'}
                        customHistoricRange={customHistoricRange}
                        onHelpClick={onHelpClick}
                    />
                </div>
            )}
        </div>
    );
};

export default React.memo(ProductStatus);
